'use client'

import { Button as ButtonPrimitive } from '@base-ui/react/button'
import { cva, type VariantProps } from 'class-variance-authority'
import { Loader2 } from 'lucide-react'
import { Children, type ReactNode } from 'react'

import { useDelayedFlag } from '@/hooks/use-delayed-flag'
import { cn } from '@/lib/utils'

// `default` is a byte-identical alias for `md` (untitledui-reference.md: "default
// → md") — a shared constant so the two variant entries can never drift apart.
const BUTTON_SIZE_MD =
  "h-10 gap-1 px-3.5 py-2.5 text-sm leading-5 has-data-[icon=inline-end]:pr-2.5 has-data-[icon=inline-start]:pl-2.5 [&_svg:not([class*='size-'])]:size-5"

// untitledui-reference.md §3 "Buttons/Button" (master 1:1183, set 3287:427074) —
// the coverage matrix is the source for every number below, not the onboarding
// mock. Ladder: xs 32 · sm 36 · md 40 · lg 44 · xl 52, `default` aliased to `md`
// so the 154 call sites that still pass no `size` (or `size="default"`) inherit
// the grown control without a rename here — T4 sweeps those. Weight is 600 at
// EVERY size (never 500); type is 14/20 (Tailwind's stock `text-sm`, which
// already resolves to font-size 14px/line-height 20px — not a custom token) for
// xs–md and 16/24 (`text-base`) for lg–xl. Icons are 16px at xs, 20px at every
// other size. radius-md (8px) at every size, including icon-only — the kit's
// separate `Buttons/Button utility` set (32/28px, 6px radius) is NOT adopted;
// icon-only stays a size of this same component (recorded divergence).
// Secondary = our `outline` variant (white bg, hairline border, gray-700-class
// text). Tertiary = our `ghost` variant (text-only muted). `link` never
// underlines — the master has no underline state.
//
// ButtonPrimitive-migration (2026-08-30): two rungs below `xs`, added when the
// last direct `@base-ui/react/button` call sites in `src/components/shared/`
// were converted to this atom. `size="chip"` (24px, text-caption/icon-3) is
// the pill-shaped toggle/action chip shape (SovChartLegend, ChartLegendToggles)
// — geometry only, no border/radius, those vary by call site. `size="auto"`
// (paired only with `variant="unstyled"`) is the bare escape hatch for a
// composite region — a whole selectable KpiCard tile, a leaderboard row —
// that needs real `<button>` semantics but owns 100% of its own visual
// treatment. Checked against the Claude Design source's own Button
// (`components/core/Button.jsx`): its ladder is xs 28 · sm 32 · default 40 ·
// lg 44, with six variants (default/outline/secondary/ghost/destructive/link)
// — a strict subset of ours (we additionally carry `neon`, marketing-only per
// DES-10, and `destructive-solid`, the DES-33 solid-stop-sign pairing the
// source's model doesn't need). The source's height numbers do NOT match this
// file's rung-for-rung (its "sm" is our "xs"; it has no `md`/`xl`) — recorded
// as a drift, not reconciled here: renumbering this ladder would touch the 154
// call sites already keyed to it, well outside this migration's scope.
//
// brand-token-migration (design correction): the Claude Design source is
// completely flat — no box-shadow anywhere, on any variant, in any state.
// The skeuomorphic `btn-gloss` (solid fills) / `btn-bevel` (outline) inset-
// shadow stacks were ported from the old pre-migration UntitledUI kit and
// never revisited; both are removed. `outline` gets a real CSS `border`
// instead, since nothing else is left to compete with it on the same pixel.
//
// Focus is the shared `focus-ring` utility (DES-39) — kept exactly as-is,
// it is a genuine accessibility feature and unrelated to the deleted bevel.
const buttonVariants = cva(
  // `disabled:` styles the native disabled attr; `aria-disabled:` styles the
  // focus-preserving "blocked/unavailable" state (rulebook §1 — keeps focus, no
  // pointer-events-none so wrapping Tooltips still work). Set aria-disabled="true"
  // + a click no-op for blocked actions; NEVER hand-roll `opacity-50 cursor-not-allowed`.
  "inline-flex shrink-0 cursor-pointer items-center justify-center rounded-md font-semibold whitespace-nowrap transition-colors outline-none select-none focus-ring active:not-aria-[haspopup]:translate-y-px disabled:pointer-events-none disabled:opacity-50 aria-disabled:cursor-not-allowed aria-disabled:opacity-50 aria-invalid:ring-error [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        // Primary — flat --primary fill, no shadow, no border (Claude Design
        // source: every button variant is completely flat). The Step-1 hero
        // CTA additionally opts into `btn-gloss-hero` via className for its
        // gradient fill — see globals.css.
        // Icon dims to 60% resting / 70% hover on every solid-fill hierarchy
        // (matrix "Primary"/"Destructive Primary" rows).
        // round-4 audit: disabled gets the source's own distinct flat pair
        // (paper-200/ink-300) rather than a dimmed lime — 50% opacity on
        // lime-200 renders a washed pale-lime, not the neutral gray disabled
        // means everywhere else. Only `default` needs this override; the
        // shared base class's opacity-50 treatment is unevidenced-but-fine
        // for the border/text-only hierarchies. The fill goes through the
        // `--accent` role (`--accent: var(--paper-200)` in light): raw
        // `bg-paper-200` has no dark value and made a disabled primary a
        // near-white pill in dark. ink-300 is a mid-grey legible on both.
        default:
          'bg-primary text-primary-foreground hover:bg-[var(--primary-gradient-start)] disabled:bg-accent disabled:text-ink-300 disabled:opacity-100 aria-disabled:bg-accent aria-disabled:text-ink-300 aria-disabled:opacity-100 [&_svg]:opacity-60 hover:[&_svg]:opacity-70',
        neon: 'bg-neon text-neon-foreground hover:bg-neon-hover',
        // White bg, hairline border, gray-700-class text per codex §8. Hover
        // fill/text are the matrix's literal values (#fafafa / #262626) —
        // --surface is the exact `bg-secondary` token (#fafafa, verified in
        // globals.css); no existing token matches #262626 more closely than
        // --foreground, already used here for the same purpose pre-T3.
        // The border was previously drawn as an inset-shadow ring inside the
        // now-deleted `btn-bevel` utility; the Claude Design source draws it
        // as a plain 1px border with no shadow, so it is a real CSS border
        // now (nothing else layers over the same pixel to fight it).
        // NOTE the token: `--foreground-secondary` (#404040, the kit's
        // `text-secondary (700)`), NOT `--secondary-foreground` — which is
        // `--gray-700`, a cooler oklch(38% .012 264). The two names are
        // anagrams and this variant had the wrong one; the right token existed
        // the whole time (ARC-22's exact shape). Verified against master
        // `11128:618651`, whose label is #404040.
        outline:
          'border border-border-strong bg-background text-foreground-secondary hover:bg-surface hover:text-foreground aria-expanded:bg-surface aria-expanded:text-foreground dark:bg-input/30 dark:hover:bg-input/50',
        // round-4 audit: hover uses the registered --ink-900-hover token
        // (globals.css) instead of opacity math — the design source's
        // "Export CSV" specimen states its hover literally as
        // `background: var(--ink-900-hover)`.
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-ink-900-hover aria-expanded:bg-secondary aria-expanded:text-secondary-foreground',
        // Text-only muted per codex §8 — matrix "Tertiary" hierarchy.
        ghost:
          'text-muted-foreground hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:hover:bg-muted/50',
        // Tint — matrix has no separate "tint" row; this is our own softened
        // reading of Destructive, kept alongside the new solid hierarchy below
        // (DES-33 needs both: tint for a low-emphasis destructive action,
        // solid for the one that needs the strongest possible stop-sign).
        // No per-variant aria-invalid override — the base class's
        // aria-invalid:ring-destructive/40 already applies to every hierarchy.
        destructive:
          'bg-destructive/10 text-destructive hover:bg-destructive/20 dark:bg-destructive/20 dark:hover:bg-destructive/30',
        // Matrix "Destructive Primary" — additive, alongside `destructive`
        // above. `--destructive` is the reference doc's documented #dc2626
        // approximation (untitledui-reference.md §2 token table); white text
        // via `--rose-foreground`, the semantic on-solid-rose token.
        'destructive-solid':
          'bg-destructive text-rose-foreground hover:bg-destructive/90 [&_svg]:opacity-60 hover:[&_svg]:opacity-70',
        // Matrix "Link color" — never underlined, no hover row defined.
        // round-4 audit: --primary (lime-200) is a surface color only, never
        // text (design source's own explicit rule) — the design doc's own
        // prose names info tone for "inline links": `text-info`, not `text-primary`.
        link: 'text-info underline-offset-4',
        // ButtonPrimitive-migration (2026-08-30): the escape hatch for a
        // composite region (a whole selectable KpiCard tile, a leaderboard
        // row) that needs real `<button>` semantics — focus-ring, `pending`,
        // `aria-disabled` — but supplies 100% of its own visual treatment via
        // `className`. Paired ONLY with `size="auto"`; the pairing exists so a
        // caller can never accidentally combine a real color hierarchy with no
        // geometry (or vice versa). See the `compoundVariants` entry below for
        // the base-class resets this pairing needs.
        unstyled: '',
      },
      size: {
        // `default` is a byte-identical alias for `md` (see file-header note) —
        // shares the string so the two can never drift.
        default: BUTTON_SIZE_MD,
        xs: "h-8 gap-1 px-2.5 py-1.5 text-sm leading-5 rounded-md in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-4",
        sm: "h-9 gap-1 px-3 py-2 text-sm leading-5 rounded-md in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2 [&_svg:not([class*='size-'])]:size-5",
        md: BUTTON_SIZE_MD,
        lg: "h-11 gap-1.5 px-4 py-2.5 text-base leading-6 has-data-[icon=inline-end]:pr-3 has-data-[icon=inline-start]:pl-3 [&_svg:not([class*='size-'])]:size-5",
        // round-4 audit: h-12 (48px) never matched the registered --control-xl
        // (52px) — the control-height ladder's own token existed and was
        // never consumed anywhere in the codebase. DES-13 exempts a
        // var()-referencing arbitrary value (same precedent as
        // h-[var(--accordion-panel-height)]).
        xl: "h-[var(--control-xl)] gap-1.5 px-4.5 py-3 text-base leading-6 has-data-[icon=inline-end]:pr-3.5 has-data-[icon=inline-start]:pl-3.5 [&_svg:not([class*='size-'])]:size-5",
        // Icon-only — a size of this same component (kit's separate 32/28px,
        // 6px-radius `Buttons/Button utility` set is not adopted; radius-md at
        // every rung, see file-header note). Unchanged by the T3 ladder — the
        // kit's icon-only geometry belongs to that unadopted separate set, and
        // these four rungs have no counterpart to re-derive from (undiffed).
        icon: 'size-8',
        'icon-xs':
          "size-6 rounded-md in-data-[slot=button-group]:rounded-lg [&_svg:not([class*='size-'])]:size-3",
        'icon-sm': 'size-7 rounded-md in-data-[slot=button-group]:rounded-lg',
        'icon-lg': 'size-9',
        // ButtonPrimitive-migration (2026-08-30): below `xs` (32px), for the
        // pill-shaped 24px chips ButtonPrimitive-conversion surfaced at three
        // call sites (SovChartLegend's competitor toggle and "+ Add
        // competitor", chart-card's ChartLegendToggles) — confirmed against
        // the Claude Design source's own InfoPopover/WhyPopover chip, also
        // 24px/`--font-sans` 11.5px (our nearest on-scale step is
        // `text-caption`, 12px). Border and radius are NOT baked in here
        // (they vary — full pill vs `rounded-sm` — by call site) so `chip` is
        // geometry only, same convention as every other size rung.
        chip: "h-6 gap-1.5 px-2 text-caption leading-4 has-data-[icon=inline-end]:pr-1 has-data-[icon=inline-start]:pl-1 [&_svg:not([class*='size-'])]:size-3",
        // Paired only with `variant="unstyled"` — see that variant's doc.
        // Deliberately empty: this rung asserts NO geometry, which is the
        // point (DES-52's "must scale font/icon with height" does not apply
        // to a rung that defines no height).
        auto: '',
      },
    },
    compoundVariants: [
      {
        // Resets the three base-class defaults that fight a composite
        // region's own layout (`rounded-md`, `font-semibold`,
        // `whitespace-nowrap`) when the caller owns 100% of the visual
        // treatment. Anything narrower (e.g. `items-center` for an
        // icon-only trigger) is left alone — those already match what an
        // `unstyled`/`auto` call site wants, and a layout-specific need
        // (KpiCard's `items-stretch` for its flex-col tile) belongs in that
        // call site's own className, not here.
        variant: 'unstyled',
        size: 'auto',
        class: 'rounded-none font-normal whitespace-normal',
      },
    ],
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

/**
 * Chrome/Google Translate replaces a bare text node with `<font>` wrappers. React
 * then throws on `insertBefore(spinner, <the original text node>)` when `pending`
 * flips, because that node is no longer a child. Wrapping each run of text/number
 * children in ONE `<span>` with ONE joined text child means the spinner is always
 * inserted before an element, and React updates the label via `textContent`.
 * A span is a flex item exactly like the old anonymous text item, so layout is
 * unchanged.
 */
function wrapTextRuns(children: ReactNode): ReactNode {
  const out: ReactNode[] = []
  let run: string[] = []
  const flush = () => {
    if (run.length === 0) return
    out.push(<span key={`text-${out.length}`}>{run.join('')}</span>)
    run = []
  }
  for (const child of Children.toArray(children)) {
    if (typeof child === 'string' || typeof child === 'number') {
      run.push(String(child))
    } else {
      flush()
      out.push(child)
    }
  }
  flush()
  return out
}

/**
 * Button — the canonical control for every clickable action in the app.
 * Sizes `xs`/`sm`/`md`(`default`)/`lg`/`xl` and icon-only `icon`/`icon-xs`/
 * `icon-sm`/`icon-lg`, plus `chip` (24px pill/toggle chip, geometry only) and
 * `auto` (no geometry — paired only with `variant="unstyled"`). Hierarchies:
 * `default` (primary, solid lime — brand-token-migration T1 repointed this
 * from indigo), `outline` (secondary, white + hairline), `secondary` (solid
 * dark-ink fill, no kit counterpart), `ghost` (tertiary, text-only),
 * `destructive` (tint), `destructive-solid` (solid stop-sign), `link`, `neon`
 * (marketing only, DES-10), `unstyled` (no color treatment — the caller owns
 * 100% of the visual style via `className`; paired only with `size="auto"`).
 *
 * WHEN: any button anywhere in the app or marketing surface. WHEN an
 * irreversible destructive action needs the strongest possible visual stop —
 * `variant="destructive-solid"` (DES-33). WHEN blocking a click while keeping
 * the control focusable/tooltippable — pass `pending`, never a hand-rolled
 * `disabled` + spinner.
 *
 * NEVER hardcode a size, radius, or type-scale value at the call site — use
 * the `size` prop. NEVER wrap with `opacity-50 cursor-not-allowed` — that's
 * `aria-disabled` (DES-28). NEVER add a second `variant="default"` in the same
 * zone (DES-32). Icon-only buttons NEVER ship without both an `aria-label` and
 * a wrapping `<Tooltip>` (DES-40).
 *
 * Rulebook §1: When pending, a Loader2 spinner replaces the leading icon slot,
 * the label stays visible, width is preserved, and clicks are blocked via
 * aria-disabled semantics (NOT `disabled` attr — keeps focus per rulebook §1).
 * This deliberately diverges from the Untitled UI kit, whose Loading state
 * hardcodes the label to "Submitting..." — our caller's label survives, and
 * the onboarding mock's own step-1 button ("Analyzing your site…") contradicts
 * the kit it was built from (untitledui-reference.md §3).
 *
 * Anti-flash: spinner appears only after ~200ms delay and stays ≥400ms once shown
 * (handled by useDelayedFlag internally).
 *
 * NEVER hand-compose Loader2 + disabled — pass `pending` instead.
 */
function Button({
  className,
  variant = 'default',
  size = 'default',
  pending,
  onClick,
  children,
  ...props
}: ButtonPrimitive.Props &
  VariantProps<typeof buttonVariants> & {
    /**
     * When true: shows spinner in leading icon slot (label stays), blocks clicks,
     * sets aria-busy + data-pending. Anti-flash: 200ms delay, 400ms min-display.
     */
    pending?: boolean
  }) {
  const showSpinner = useDelayedFlag(pending ?? false)

  return (
    <ButtonPrimitive
      data-slot="button"
      data-pending={pending ? 'true' : undefined}
      aria-busy={pending ? 'true' : undefined}
      // Use focusableWhenDisabled so the button stays in the focus ring
      // while blocking interaction — per rulebook §1 (aria-disabled semantics).
      disabled={pending ? true : (props.disabled ?? false)}
      focusableWhenDisabled={pending ? true : undefined}
      onClick={pending ? undefined : onClick}
      className={cn(
        buttonVariants({ variant, size, className }),
        pending && 'pointer-events-none opacity-70'
      )}
      {...props}
    >
      {showSpinner && (
        <Loader2 aria-hidden="true" className="animate-spin" data-icon="inline-start" />
      )}
      {wrapTextRuns(children)}
    </ButtonPrimitive>
  )
}

export { Button, buttonVariants }
